import { Box, SxProps } from '@mui/material';
import cn from 'classnames';
import getSymbolFromCurrency from 'currency-symbol-map';
import * as React from 'react';
import { FC, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';

import { FALLBACK_CURRENCY_CODE } from '../../constants/shell';
import { useCurrency } from '../../contexts/CurrencyProvider';
import { IResultPriceWithCurrency } from '../../types/shell';

import styles from './Price.module.scss';

interface IProps {
  className?: string;
  sx?: SxProps;
  hint?: string;
  value: IResultPriceWithCurrency;
  thousandSeparator?: boolean | string;
  fixedDecimalScale?: boolean;
  decimalScale?: number;
  hyphenOnZero?: boolean;
  big?: boolean;
  amountClassName?: string;
}

const Price: FC<IProps> = ({
  value,
  className,
  hint = '',
  thousandSeparator,
  fixedDecimalScale = true,
  decimalScale = 2,
  hyphenOnZero,
  big,
  amountClassName,
  sx,
}: IProps) => {
  const { requiredCurrency } = useCurrency();

  const currencySymbol = useMemo(() => {
    let { currency } = value;
    if (!currency) {
      console.warn('Currency is not provided for Price', value.price, '. Falling back to', FALLBACK_CURRENCY_CODE);
      currency = requiredCurrency;
    }

    return getSymbolFromCurrency(currency);
  }, [value, requiredCurrency]);

  return (
    <Box component="div" title={hint} className={cn(styles.price, className, { [styles.big]: big })} sx={sx}>
      {hyphenOnZero && !value ? (
        <span className={styles.amount} data-action="price_amount">
          {currencySymbol}-.--
        </span>
      ) : (
        <NumericFormat
          className={cn(styles.amount, amountClassName)}
          prefix={currencySymbol}
          value={value.price}
          displayType="text"
          thousandSeparator={thousandSeparator}
          decimalScale={decimalScale}
          fixedDecimalScale={fixedDecimalScale}
          data-action="price_amount"
        />
      )}
    </Box>
  );
};

export default Price;
